import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import {
  Phone, Upload, Play, Pause, Square, RefreshCw, Download, FlaskConical,
  Settings as SettingsIcon, Loader2, ShieldAlert, DollarSign, Search, CheckCircle2,
  AlertTriangle, PhoneCall, Users, Ban, History, Clock, Hash, ListChecks,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import OscarPaymentsTab from './OscarPaymentsTab';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const BAL_BADGE = {
  zero: { label: 'Zero', cls: 'bg-emerald-100 text-emerald-700' },
  due: { label: 'Due', cls: 'bg-amber-100 text-amber-700' },
  review: { label: 'Review', cls: 'bg-rose-100 text-rose-700' },
  unknown: { label: 'Unknown', cls: 'bg-slate-100 text-slate-600' },
};
const CALL_BADGE = {
  queued: 'bg-slate-100 text-slate-600',
  calling: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
};
const PAY_BADGE = {
  not_attempted: 'bg-slate-100 text-slate-500',
  approved: 'bg-indigo-100 text-indigo-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  review_required: 'bg-amber-100 text-amber-700',
};

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={`oscar-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tint || 'bg-slate-100 text-slate-600'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </Card>
  );
}

export default function OscarPage() {
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [batches, setBatches] = useState([]);
  const [members, setMembers] = useState({ total: 0, members: [] });
  const [search, setSearch] = useState('');
  const [filterBatch, setFilterBatch] = useState('all');
  const [filterBal, setFilterBal] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importName, setImportName] = useState('');

  // simulator
  const [simPrompts, setSimPrompts] = useState(
    'Thank you for calling Oscar. For English, press 1.\nPlease enter your 10 digit member ID.\nYour current outstanding balance is $177.99.'
  );
  const [simMember, setSimMember] = useState('1234567890');
  const [simResult, setSimResult] = useState(null);

  // settings
  const [settings, setSettings] = useState(null);

  // call detail modal + history
  const [detail, setDetail] = useState(null); // {member, calls}
  const [detailOpen, setDetailOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, m] = await Promise.all([
        portalApi.oscarDashboard(),
        portalApi.oscarBatches(),
        portalApi.oscarMembers({
          search,
          batch_id: filterBatch !== 'all' ? filterBatch : undefined,
          balance_status: filterBal !== 'all' ? filterBal : undefined,
        }),
      ]);
      setStats(s); setBatches(b); setMembers(m);
      portalApi.oscarMetrics().then(setMetrics).catch(() => {});
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [search, filterBatch, filterBal]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { portalApi.oscarSettings().then(setSettings).catch(() => {}); }, []);

  // auto-refresh while any batch is running
  useEffect(() => {
    const running = batches.some((b) => b.status === 'running');
    if (!running) return;
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, [batches, loadAll]);

  const doImport = async () => {
    const rows = importText.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
      const parts = line.split(/[,\t]/).map((p) => p.trim());
      return { member_id: parts[0], member_name: '', amount: parts[1] || '' };
    });
    if (!rows.length) { toast.error('Paste at least one Member ID'); return; }
    setBusy('import');
    try {
      const res = await portalApi.oscarImport({ batch_name: importName || undefined, rows });
      toast.success(`Imported ${res.imported} members${res.skipped.length ? `, ${res.skipped.length} skipped` : ''}`);
      setImportOpen(false); setImportText(''); setImportName('');
      await loadAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const batchAction = async (id, action) => {
    setBusy(id + action);
    try {
      if (action === 'start') await portalApi.oscarStartBatch(id);
      if (action === 'pause') await portalApi.oscarPauseBatch(id);
      if (action === 'stop') await portalApi.oscarStopBatch(id);
      if (action === 'retry') await portalApi.oscarRetryFailed(id);
      toast.success(`Batch ${action}`);
      await loadAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const runSim = async () => {
    setBusy('sim');
    try {
      const res = await portalApi.oscarSimulate({
        member_id: simMember,
        prompts: simPrompts.split('\n').map((p) => p.trim()).filter(Boolean),
      });
      setSimResult(res);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const openDetail = async (member) => {
    setDetail({ member, calls: null }); setDetailOpen(true);
    try {
      const data = await portalApi.oscarMemberDetail(member.id);
      setDetail(data);
    } catch (e) { toast.error(e.message); }
  };

  const retryMember = async (id) => {
    setBusy('retry');
    try {
      await portalApi.oscarRetryMember(id);
      toast.success('Balance check re-queued — calling now');
      await loadAll();
      const data = await portalApi.oscarMemberDetail(id);
      setDetail(data);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const loadHistory = async () => {
    try { setHistory(await portalApi.oscarCallHistory({ limit: 100 })); }
    catch (e) { toast.error(e.message); }
  };

  const SIM_PRESETS = {
    'Zero balance': 'For English, press 1.\nPlease enter your 10 digit member ID.\nYou currently have no outstanding balance with Oscar.',
    'Outstanding balance': 'For English, press 1.\nTo access billing and make a payment, press 1.\nPlease enter your member ID.\nYour current outstanding balance is $177.99.',
    'Unexpected prompt': 'For English, press 1.\nWe are experiencing technical difficulties, please try again later.',
    'Member ID failure': 'For English, press 1.\nPlease enter your member ID.\nThe member ID you entered was not found. Please try again.',
    'Silence / timeout': 'For English, press 1.\n\n\n',
    'Saved payment method': 'For English, press 1.\nWe have a bank account on file ending in 1234. Press 1 to use it.',
  };

  const saveSettings = async () => {
    setBusy('settings');
    try {
      const res = await portalApi.oscarUpdateSettings({
        concurrency: Number(settings.concurrency),
        call_timeout: Number(settings.call_timeout),
        target_number: settings.target_number,
        nav_mode: settings.nav_mode,
        fast_sequence: settings.fast_sequence,
        member_id_pause_ms: Number(settings.member_id_pause_ms),
        delete_recordings: settings.delete_recordings,
      });
      setSettings(res); toast.success('Settings saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const purgeRecordings = async () => {
    setBusy('purge');
    try {
      const r = await portalApi.oscarPurgeRecordings();
      toast.success(`Deleted ${r.deleted} recording(s)`);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const resetAllData = async () => {
    if (!window.confirm('Delete ALL Oscar test data — every imported member, batch, call and payment record (and their recordings)? This cannot be undone.')) return;
    if (!window.confirm('Final confirmation: permanently wipe all Oscar test data now?')) return;
    setBusy('reset');
    try {
      const r = await portalApi.oscarResetData({ confirm: true, delete_recordings: true });
      const d = r.deleted || {};
      toast.success(`Wiped: ${d.members || 0} members, ${d.batches || 0} batches, ${d.calls || 0} calls, ${d.payments || 0} payments, ${d.recordings || 0} recordings`);
      await loadAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>;
  }

  const TABS = [
    { k: 'dashboard', label: 'Dashboard', icon: Phone },
    { k: 'batches', label: 'Batches', icon: PhoneCall },
    { k: 'payments', label: 'Payments', icon: DollarSign },
    { k: 'history', label: 'Call History', icon: History },
    { k: 'simulate', label: 'Test IVR', icon: FlaskConical },
    { k: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="space-y-6" data-testid="oscar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Phone className="h-7 w-7 text-sky-600" /> Oscar Premium Payment
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Automated balance audit via Twilio voice calls to Oscar Health's IVR.</p>
        </div>
        <div className="flex items-center gap-2">
          {stats && !stats.twilio_configured && (
            <Badge className="bg-amber-100 text-amber-800 gap-1" data-testid="oscar-twilio-warning">
              <AlertTriangle className="h-3 w-3" /> Twilio not configured
            </Badge>
          )}
          <Badge
            data-testid="oscar-live-ach-status"
            className={`gap-1.5 ${stats && stats.live_payments_enabled ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
            <span className={`h-2 w-2 rounded-full ${stats && stats.live_payments_enabled ? 'bg-emerald-200' : 'bg-slate-400'}`} />
            {stats && stats.live_payments_enabled ? 'LIVE ACH ENABLED' : 'LIVE ACH DISABLED'}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); if (t.k === 'history') loadHistory(); }} data-testid={`oscar-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === t.k ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Stat icon={Users} label="Total Members" value={stats.total_members} tint="bg-slate-100 text-slate-700" />
            <Stat icon={PhoneCall} label="Awaiting" value={stats.awaiting} tint="bg-slate-100 text-slate-600" />
            <Stat icon={Loader2} label="Calling" value={stats.calling} tint="bg-sky-100 text-sky-700" />
            <Stat icon={CheckCircle2} label="Zero Balance" value={stats.zero_balance} tint="bg-emerald-100 text-emerald-700" />
            <Stat icon={DollarSign} label="Balance Due" value={stats.balance_due} tint="bg-amber-100 text-amber-700" />
            <Stat icon={DollarSign} label="Total Outstanding" value={`$${(stats.total_outstanding || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} tint="bg-amber-100 text-amber-700" />
            <Stat icon={ListChecks} label="Audits Complete" value={stats.audits_complete} tint="bg-emerald-100 text-emerald-700" />
            <Stat icon={AlertTriangle} label="Review Required" value={stats.review_required} tint="bg-rose-100 text-rose-700" />
            <Stat icon={PhoneCall} label="Completed Calls" value={stats.completed_calls} tint="bg-slate-100 text-slate-600" />
            <Stat icon={Ban} label="Failed Calls" value={stats.failed_calls} tint="bg-rose-100 text-rose-700" />
            <Stat icon={CheckCircle2} label="Payments Confirmed" value={stats.payments_confirmed} tint="bg-slate-100 text-slate-500" />
          </div>

          {metrics && metrics.total_calls > 0 && (
            <Card className="p-4" data-testid="oscar-metrics">
              <div className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-sky-600" /> Call Optimization Metrics</div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
                {[
                  ['Avg Call Duration', metrics.avg_call_duration_sec == null ? '—' : `${metrics.avg_call_duration_sec}s`],
                  ['Avg → Member ID', metrics.avg_time_to_member_id_sec == null ? '—' : `${metrics.avg_time_to_member_id_sec}s`],
                  ['Avg → Balance', metrics.avg_time_to_balance_sec == null ? '—' : `${metrics.avg_time_to_balance_sec}s`],
                  ['Twilio Cost', metrics.cost_data_available ? `${metrics.twilio_total_cost} ${metrics.twilio_price_unit}` : 'n/a'],
                  ['Success / 100', metrics.success_per_100_calls == null ? '—' : metrics.success_per_100_calls],
                  ['Cost / Success', metrics.avg_cost_per_successful_audit == null ? '—' : `${metrics.avg_cost_per_successful_audit} ${metrics.twilio_price_unit}`],
                  ['Fast-nav Fail %', metrics.fast_nav_failure_rate_pct == null ? '—' : `${metrics.fast_nav_failure_rate_pct}%`],
                ].map(([label, val]) => (
                  <div key={label} className="bg-slate-50 rounded-lg p-2" data-testid={`oscar-metric-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <div className="text-lg font-bold text-slate-900 leading-none">{val}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Member table + controls */}
          <Card className="p-0 overflow-hidden">
            <div className="p-4 flex flex-wrap items-center gap-2 border-b border-slate-100">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search member ID or name"
                  className="pl-9" data-testid="oscar-member-search" />
              </div>
              <Select value={filterBatch} onValueChange={setFilterBatch}>
                <SelectTrigger className="w-44" data-testid="oscar-filter-batch"><SelectValue placeholder="Batch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All batches</SelectItem>
                  {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterBal} onValueChange={setFilterBal}>
                <SelectTrigger className="w-40" data-testid="oscar-filter-balance"><SelectValue placeholder="Balance" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="zero">Zero</SelectItem>
                  <SelectItem value="due">Due</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="oscar-import-btn">
                <Upload className="h-4 w-4 mr-1" /> Import CSV
              </Button>
              <Button variant="outline" onClick={() => portalApi.oscarExport(filterBatch !== 'all' ? filterBatch : null)} data-testid="oscar-export-btn">
                <Download className="h-4 w-4 mr-1" /> Export
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    {['Member ID', 'Name', 'Balance', 'Balance Status', 'Audit', 'Call', 'Last Checked', 'Confirmation', 'Notes'].map((h) => (
                      <th key={h} className="text-left px-4 py-2 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="oscar-member-table">
                  {members.members.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No members. Import a CSV to begin.</td></tr>
                  )}
                  {members.members.map((m) => (
                    <tr key={m.id} onClick={() => openDetail(m)} className="border-t border-slate-100 hover:bg-sky-50/60 cursor-pointer" data-testid={`oscar-member-row-${m.id}`}>
                      <td className="px-4 py-2 font-mono text-sky-700 underline-offset-2 hover:underline">{m.member_id}</td>
                      <td className="px-4 py-2">{m.member_name || '—'}</td>
                      <td className="px-4 py-2">{m.balance_amount == null ? '—' : `$${m.balance_amount.toFixed(2)}`}</td>
                      <td className="px-4 py-2"><Badge className={BAL_BADGE[m.balance_status]?.cls}>{BAL_BADGE[m.balance_status]?.label || m.balance_status}</Badge></td>
                      <td className="px-4 py-2 text-xs capitalize text-slate-600">{m.audit_status || 'pending'}</td>
                      <td className="px-4 py-2"><Badge className={CALL_BADGE[m.call_status] || ''}>{m.call_status}</Badge></td>
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{m.last_checked ? new Date(m.last_checked).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs">{m.confirmation_ref || '—'}</td>
                      <td className="px-4 py-2 text-slate-500 max-w-xs truncate" title={m.notes}>{m.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">{members.total} member(s)</div>
          </Card>
        </div>
      )}

      {tab === 'batches' && (
        <div className="space-y-3" data-testid="oscar-batches-panel">
          {batches.length === 0 && (
            <Card className="p-10 text-center text-slate-400">
              No batches yet. <button className="text-sky-600 underline" onClick={() => setImportOpen(true)}>Import a CSV</button> to create one.
            </Card>
          )}
          {batches.map((b) => (
            <Card key={b.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`oscar-batch-${b.id}`}>
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {b.name}
                  <Badge className={b.status === 'running' ? 'bg-sky-100 text-sky-700' : b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{b.status}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {b.total} total · {b.queued} queued · {b.calling} calling · {b.completed} completed · {b.failed} failed
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(b.status === 'idle' || b.status === 'paused' || b.status === 'stopped') && (
                  <Button size="sm" onClick={() => batchAction(b.id, 'start')} disabled={busy === b.id + 'start'} data-testid={`oscar-start-${b.id}`}>
                    {busy === b.id + 'start' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Start
                  </Button>
                )}
                {b.status === 'running' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => batchAction(b.id, 'pause')} data-testid={`oscar-pause-${b.id}`}><Pause className="h-4 w-4 mr-1" /> Pause</Button>
                    <Button size="sm" variant="outline" onClick={() => batchAction(b.id, 'stop')} data-testid={`oscar-stop-${b.id}`}><Square className="h-4 w-4 mr-1" /> Stop</Button>
                  </>
                )}
                {b.failed > 0 && (
                  <Button size="sm" variant="outline" onClick={() => batchAction(b.id, 'retry')} data-testid={`oscar-retry-${b.id}`}><RefreshCw className="h-4 w-4 mr-1" /> Retry failed</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'payments' && <OscarPaymentsTab />}

      {tab === 'history' && (
        <Card className="p-0 overflow-hidden" data-testid="oscar-history-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><History className="h-4 w-4 text-sky-600" /> Call History</div>
            <Button size="sm" variant="outline" onClick={loadHistory}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>{['Member ID', 'Call SID', 'Started', 'Duration', 'Result', 'Termination'].map((h) => (
                  <th key={h} className="text-left px-4 py-2 font-medium whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No calls yet.</td></tr>}
                {history.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-2 font-mono">{c.member_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">{c.call_sid || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{c.started_at ? new Date(c.started_at).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2">{c.duration_seconds == null ? '—' : `${c.duration_seconds}s`}</td>
                    <td className="px-4 py-2"><Badge className={BAL_BADGE[c.result]?.cls || 'bg-slate-100 text-slate-600'}>{c.result}</Badge></td>
                    <td className="px-4 py-2 text-slate-500 max-w-md truncate" title={c.termination_reason}>{c.termination_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'simulate' && (
        <Card className="p-6 space-y-4" data-testid="oscar-simulator">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><FlaskConical className="h-4 w-4 text-sky-600" /> IVR Simulator</h3>
            <p className="text-sm text-slate-500 mt-1">Test the navigation + balance-detection logic with mock Oscar prompts (one per line).</p>
            <div className="mt-2 inline-flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="oscar-sim-banner">
              <FlaskConical className="h-3 w-3" /> Simulation only — does NOT place a real call and is never counted as a completed call.
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="oscar-sim-presets">
            {Object.keys(SIM_PRESETS).map((name) => (
              <Button key={name} size="sm" variant="outline" onClick={() => { setSimPrompts(SIM_PRESETS[name]); setSimResult(null); }}
                data-testid={`oscar-sim-preset-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{name}</Button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Member ID</Label>
              <Input value={simMember} onChange={(e) => setSimMember(e.target.value)} data-testid="oscar-sim-member" />
              <Label>Mock IVR prompts (one per line)</Label>
              <Textarea rows={8} value={simPrompts} onChange={(e) => setSimPrompts(e.target.value)} data-testid="oscar-sim-prompts" className="font-mono text-xs" />
              <Button onClick={runSim} disabled={busy === 'sim'} data-testid="oscar-sim-run">
                {busy === 'sim' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Run simulation
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Result</Label>
              {!simResult && <div className="text-sm text-slate-400 border rounded-lg p-4">Run a simulation to see each decision.</div>}
              {simResult && (
                <div className="border rounded-lg divide-y" data-testid="oscar-sim-result">
                  <div className="p-3 flex items-center gap-2">
                    <Badge className={BAL_BADGE[simResult.balance_status]?.cls}>{BAL_BADGE[simResult.balance_status]?.label}</Badge>
                    <span className="text-sm font-medium">{simResult.balance_amount == null ? 'No amount' : `$${simResult.balance_amount.toFixed(2)}`}</span>
                  </div>
                  {simResult.steps.map((s, i) => (
                    <div key={i} className="p-3 text-xs">
                      <div className="text-slate-500 italic">"{s.prompt}"</div>
                      <div className="mt-1 font-medium text-slate-800">
                        {s.action === 'send_digits' && <>→ Send DTMF <code className="bg-slate-100 px-1 rounded">{s.digits}</code> ({s.rule})</>}
                        {s.action === 'hangup' && <>→ End call · <span className="text-rose-700">{s.note}</span></>}
                        {s.action === 'unknown' && <>→ {s.note}</>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {tab === 'settings' && settings && (
        <Card className="p-6 space-y-5 max-w-2xl" data-testid="oscar-settings-panel">
          <div className="flex items-center justify-between p-3 rounded-lg bg-rose-50 border border-rose-200">
            <div>
              <div className="font-medium text-rose-900 flex items-center gap-2"><Ban className="h-4 w-4" /> Payment processing</div>
              <p className="text-xs text-rose-700 mt-0.5">Disabled by default in Phase 1. Enabling requires authorization & compliance review (not available in this release).</p>
            </div>
            <Badge className="bg-rose-600 text-white">{settings.payments_enabled ? 'ENABLED' : 'DISABLED'}</Badge>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Simultaneous calls (concurrency)</Label>
              <Input type="number" min={1} max={25} value={settings.concurrency}
                onChange={(e) => setSettings({ ...settings, concurrency: e.target.value })} data-testid="oscar-set-concurrency" />
            </div>
            <div className="space-y-1">
              <Label>Call timeout (seconds)</Label>
              <Input type="number" value={settings.call_timeout}
                onChange={(e) => setSettings({ ...settings, call_timeout: e.target.value })} data-testid="oscar-set-timeout" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Oscar target phone number</Label>
              <Input value={settings.target_number}
                onChange={(e) => setSettings({ ...settings, target_number: e.target.value })} data-testid="oscar-set-target" />
            </div>
          </div>

          {/* Hybrid navigation engine */}
          <div className="border-t pt-4 space-y-3">
            <div className="font-medium text-slate-800 flex items-center gap-2"><Hash className="h-4 w-4 text-sky-600" /> IVR Navigation Engine</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Navigation mode</Label>
                <Select value={settings.nav_mode} onValueChange={(v) => setSettings({ ...settings, nav_mode: v })}>
                  <SelectTrigger data-testid="oscar-set-navmode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hybrid">Hybrid (fast DTMF + listen) — recommended</SelectItem>
                    <SelectItem value="listen">Listen only (prompt-aware)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Pause before Member ID (ms) — blind fallback</Label>
                <Input type="number" value={settings.member_id_pause_ms}
                  onChange={(e) => setSettings({ ...settings, member_id_pause_ms: e.target.value })} data-testid="oscar-set-midpause" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Fast DTMF sequence (digits : pause-ms per step, one per line)</Label>
              <Textarea rows={3} className="font-mono text-xs" data-testid="oscar-set-fastseq"
                value={(settings.fast_sequence || []).map((s) => `${s.digits}:${s.pause_ms}`).join('\n')}
                onChange={(e) => setSettings({
                  ...settings,
                  fast_sequence: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
                    const [d, p] = l.split(':');
                    return { digits: (d || '').trim(), pause_ms: parseInt(p, 10) || 3000 };
                  }),
                })} />
              <p className="text-[11px] text-slate-500">Tested path: <code>3:4000</code> / <code>1:3000</code> / <code>1:3000</code>. Tune pauses only if the next stage is reached reliably.</p>
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="font-medium text-slate-800 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-sky-600" /> Recording & Compliance</div>
            <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="oscar-set-delrec">
              <input type="checkbox" className="h-4 w-4" checked={!!settings.delete_recordings}
                onChange={(e) => setSettings({ ...settings, delete_recordings: e.target.checked })} />
              <span>Auto-delete each Twilio recording right after Whisper transcription (recommended for compliance)</span>
            </label>
            <Button size="sm" variant="outline" onClick={purgeRecordings} disabled={busy === 'purge'} data-testid="oscar-purge-recordings">
              {busy === 'purge' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />} Purge existing recordings now
            </Button>
          </div>

          <div className="border-t pt-4 space-y-2">
            <div className="font-medium text-rose-700 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Danger zone — delete all test data</div>
            <p className="text-xs text-slate-500">Permanently removes every imported member, batch, call and payment record created while testing (and deletes their Twilio recordings). Does not affect any other part of the CRM.</p>
            <Button size="sm" variant="destructive" onClick={resetAllData} disabled={busy === 'reset'} data-testid="oscar-reset-data">
              {busy === 'reset' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />} Delete all Oscar test data
            </Button>
          </div>

          <div className="text-xs text-slate-500">
            Twilio: {settings.twilio_configured
              ? <span className="text-emerald-600 font-medium">configured ✓</span>
              : <span className="text-amber-600 font-medium">not configured — set credentials server-side before starting calls</span>}
          </div>
          <Button onClick={saveSettings} disabled={busy === 'settings'} data-testid="oscar-save-settings">
            {busy === 'settings' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save settings
          </Button>
        </Card>
      )}

      {/* Member call-detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="oscar-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-sky-600" />
              Member {detail?.member?.member_id}
            </DialogTitle>
            <DialogDescription>{detail?.member?.member_name || 'Call execution history'}</DialogDescription>
          </DialogHeader>
          {detail?.member && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-slate-400">Balance</div><div className="font-semibold">{detail.member.balance_amount == null ? '—' : `$${detail.member.balance_amount.toFixed(2)}`}</div></div>
                <div><div className="text-xs text-slate-400">Balance Status</div><Badge className={BAL_BADGE[detail.member.balance_status]?.cls}>{BAL_BADGE[detail.member.balance_status]?.label}</Badge></div>
                <div><div className="text-xs text-slate-400">Audit</div><div className="capitalize">{detail.member.audit_status || 'pending'}</div></div>
                <div><div className="text-xs text-slate-400">Call</div><Badge className={CALL_BADGE[detail.member.call_status]}>{detail.member.call_status}</Badge></div>
              </div>
              <Button size="sm" onClick={() => retryMember(detail.member.id)} disabled={busy === 'retry'} data-testid="oscar-detail-retry">
                {busy === 'retry' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Retry Balance Check
              </Button>

              {detail.calls === null && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading call history…</div>}
              {detail.calls && detail.calls.length === 0 && <div className="text-sm text-slate-400">No call attempts yet.</div>}
              {detail.calls && detail.calls.map((c, idx) => (
                <div key={c.id} className="border rounded-lg p-3 space-y-2" data-testid={`oscar-detail-call-${idx}`}>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1 text-slate-500"><Hash className="h-3 w-3" /> SID: <span className="font-mono text-slate-700">{c.call_sid || '—'}</span></div>
                    <div className="flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> {c.duration_seconds == null ? '—' : `${c.duration_seconds}s`} · {c.started_at ? new Date(c.started_at).toLocaleTimeString() : '—'}</div>
                    <div className="text-slate-500">Result: <Badge className={BAL_BADGE[c.result]?.cls || 'bg-slate-100 text-slate-600'}>{c.result}</Badge></div>
                    <div className="text-slate-500">Twilio: {c.twilio_status || '—'} · answered: {c.answered ? 'yes' : 'no'}</div>
                  </div>
                  {c.termination_reason && <div className="text-xs text-rose-700 bg-rose-50 rounded px-2 py-1">{c.termination_reason}</div>}
                  {c.whisper_balance != null && (
                    <div className="text-xs text-emerald-800 bg-emerald-50 rounded px-2 py-1" data-testid={`oscar-whisper-${idx}`}>
                      Recording verified (Whisper): <strong>${Number(c.whisper_balance).toFixed(2)}</strong>
                      {c.whisper_transcript && <span className="text-emerald-600 italic"> — "{c.whisper_transcript}"</span>}
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-slate-600 mb-1">Timestamped IVR transcript & actions</div>
                    {(!c.transcript || c.transcript.length === 0) && <div className="text-xs text-slate-400">No prompts recognized.</div>}
                    <div className="space-y-1">
                      {(c.transcript || []).map((t, i) => (
                        <div key={i} className="text-xs border-l-2 border-slate-200 pl-2">
                          <span className="text-slate-400">{t.at ? new Date(t.at).toLocaleTimeString() : ''}</span>
                          <span className="text-slate-600 italic"> "{t.prompt}"</span>
                          <div className="text-slate-800 font-medium">→ {t.detail || t.action}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="oscar-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Members</DialogTitle>
            <DialogDescription>Paste one member per line: <code>MemberID, Amount</code>. Member ID must be 10 digits; Amount is the dollar value to pay (two decimals). On Compare, a verified balance exactly equal to this amount is eligible to pay; any mismatch is flagged for review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Batch name (optional)</Label>
              <Input value={importName} onChange={(e) => setImportName(e.target.value)} placeholder="e.g. October renewals" data-testid="oscar-import-name" />
            </div>
            <div className="space-y-1">
              <Label>Members</Label>
              <Textarea rows={8} value={importText} onChange={(e) => setImportText(e.target.value)}
                placeholder={'1234567890, 50.16\n0987654321, 120.00'} className="font-mono text-xs" data-testid="oscar-import-textarea" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={doImport} disabled={busy === 'import'} data-testid="oscar-import-submit">
              {busy === 'import' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
